import { useState } from "react";
import { useAsync } from "@/components/useAsync";
import { fetchInterventionHistory, type HistoryEvent } from "@/data/interventionHistoryRepository";
import { useApp } from "@/app/AppContext";
import { customFieldLabel } from "@/data/customFieldsRepository";
import { useI18n } from "@/i18n/useI18n";
import type { TranslationKey } from "@/config/translationKeys";

const actionLabels: Record<HistoryEvent["action"], TranslationKey> = {
  created: "historyCreated",
  updated: "historyUpdated",
  status: "historyStatus",
  deleted: "historyDeleted",
  custom_field: "historyCustomField",
  comment: "historyComment",
  questionnaire: "historyQuestionnaire",
};

const PAGE_SIZE = 20;

function renderValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

export function InterventionHistory({ interventionId }: { interventionId: string }) {
  const { t, locale, language } = useI18n();
  const { data } = useApp();
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [rows, reload] = useAsync(
    interventionId + ":" + page,
    () => fetchInterventionHistory(interventionId, page * PAGE_SIZE),
  );

  return (
    <section className="intervention-history" aria-label={t("changeHistory")}>
      <h4>{t("changeHistory")}</h4>
      {rows.status === "loading" && <p aria-busy="true">…</p>}
      {rows.status === "error" && (
        <p className="auth-gate-message error" role="alert">
          {t("historyLoadError")} ({rows.error.message})
          <button type="button" onClick={() => void reload()}>↻</button>
        </p>
      )}
      {rows.status === "ready" && rows.value.length === 0 && <p>{t("historyEmpty")}</p>}
      {rows.status === "ready" && (
        <>
          <ol className="intervention-history-list">
            {rows.value.map((event) => (
              <li key={event.id}>
                <button
                  type="button"
                  className="intervention-history-event"
                  aria-expanded={expanded.includes(event.id)}
                  onClick={() =>
                    setExpanded((current) =>
                      current.includes(event.id)
                        ? current.filter((id) => id !== event.id)
                        : [...current, event.id],
                    )
                  }
                >
                  <strong>{t(actionLabels[event.action])}</strong>
                  <span>
                    {new Date(event.created_at).toLocaleString(locale)}
                    {" · "}{event.actor_email || (event.source === "import/system"
                      ? "System/import" : t("updatedByUnknown"))}
                  </span>
                </button>
                {expanded.includes(event.id) && (
                  <div className="intervention-history-changes">
                    {Object.entries(event.changes).length === 0 ? (
                      <p>{t(actionLabels[event.action])}</p>
                    ) : (
                      <dl>
                        {Object.entries(event.changes).map(([key, change]) => {
                          const customId = key.startsWith("customField:") ? key.slice(12) : null;
                          const field = customId
                            ? data.settings.customFields.find((candidate) => candidate.id === customId)
                            : undefined;
                          const label = field
                            ? customFieldLabel(field, language, data.settings.dictionary)
                            : key.startsWith("questionnaire:")
                              ? `${t("historyChangedField")}: ${key.slice(14)}`
                              : key;
                          return (
                            <div key={key}>
                              <dt>{label}</dt>
                              <dd>
                                {change.changed
                                  ? typeof change.old_length === "number" &&
                                    typeof change.new_length === "number"
                                    ? `${change.old_length.toLocaleString(locale)} ${t("historyCharacters")} → ${change.new_length.toLocaleString(locale)} ${t("historyCharacters")}`
                                    : t("historyChangedField")
                                  : `${renderValue(change.old)} → ${renderValue(change.new)}`}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ol>
          {rows.value.length === page * PAGE_SIZE && (
            <button type="button" onClick={() => setPage((value) => value + 1)}>
              {t("historyMore")}
            </button>
          )}
        </>
      )}
    </section>
  );
}
